import { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import {
  buildBracketPartsFromHtml,
  mergeBracketPartsToSigningHtml,
} from '../lib/agreementBracketFields'
import { resizeAutoGrowTextArea } from '../lib/autoGrowTextArea'

/** Inline bracket slots (textarea preferred; input kept for older cached shells). */
const BF_SLOT_FIELD_SELECTOR = '[data-bf-slot].agreement-bracket-input'

/**
 * Recipient signing: one sanitized HTML shell with real inline inputs where [fields] were,
 * so inputs stay inside headings/sentences (splitting HTML + React siblings broke layout).
 *
 * @param {string} sanitizedHtml ,  DOMPurify-sanitized agreement HTML
 * @param {Record<number, string>} values
 * @param {(next: Record<number, string> | ((prev: Record<number, string>) => Record<number, string>)) => void} onValuesChange
 * @param {number[]} invalidFieldIds ,  slot indices to show error styling (empty required fields)
 */
export function RecipientBracketAgreement({ sanitizedHtml, values, onValuesChange, invalidFieldIds = [] }) {
  const parts = useMemo(() => buildBracketPartsFromHtml(sanitizedHtml, true), [sanitizedHtml])
  const signingHtml = useMemo(() => mergeBracketPartsToSigningHtml(parts), [parts])
  const shellRef = useRef(null)

  useLayoutEffect(() => {
    const el = shellRef.current
    if (!el) return
    el.innerHTML = signingHtml
    el.querySelectorAll(BF_SLOT_FIELD_SELECTOR).forEach((node) => {
      if (node.tagName === 'TEXTAREA') resizeAutoGrowTextArea(node)
    })
  }, [signingHtml])

  useEffect(() => {
    const root = shellRef.current
    if (!root) return
    root.querySelectorAll(BF_SLOT_FIELD_SELECTOR).forEach((fieldEl) => {
      const i = Number(fieldEl.getAttribute('data-bf-slot'))
      if (Number.isNaN(i)) return
      const v = values[i] ?? ''
      if (fieldEl.value !== v) fieldEl.value = v
      if (fieldEl.tagName === 'TEXTAREA') resizeAutoGrowTextArea(fieldEl)
    })
  }, [values, signingHtml])

  useEffect(() => {
    const root = shellRef.current
    if (!root) return
    const invalidSet = new Set(invalidFieldIds)
    root.querySelectorAll(BF_SLOT_FIELD_SELECTOR).forEach((fieldEl) => {
      const i = Number(fieldEl.getAttribute('data-bf-slot'))
      fieldEl.classList.toggle('agreement-input-error', invalidSet.has(i))
      fieldEl.setAttribute('aria-invalid', invalidSet.has(i) ? 'true' : 'false')
    })
  }, [invalidFieldIds, signingHtml])

  const onInput = useCallback(
    (e) => {
      const t = e.target
      if (!t || !t.matches(BF_SLOT_FIELD_SELECTOR)) return
      if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA') return
      const i = Number(t.getAttribute('data-bf-slot'))
      if (Number.isNaN(i)) return
      if (t.tagName === 'TEXTAREA') resizeAutoGrowTextArea(t)
      onValuesChange((prev) => ({ ...prev, [i]: t.value }))
    },
    [onValuesChange],
  )

  return (
    <div
      ref={shellRef}
      className="agreement-text agreement-text--recipient-fields"
      role="article"
      onInput={onInput}
    />
  )
}
